/**
 * The single confirm-dialog primitive for every app (BL-1246, ratifying the
 * `ConfirmModal` row of the ADR 030 Canon).
 *
 * The merge collapses two implementations of the same job — admin-web's
 * `ConfirmDialog` (44 importing files) and bidder-web's `ConfirmModal` (3).
 * Both already implemented the identical await-then-close-on-success contract;
 * only presentation differed, which is exactly what dialect tokens carry. Per
 * the Canon the canonical shape is admin's icon-led layout (the danger cue
 * reads faster in a back-office context) plus bidder's inline error box.
 *
 * WHY THIS FILE EXPORTS TWO THINGS rather than one component:
 *
 *   1. `useConfirmAction` — the CONTRACT. Awaiting `onConfirm`, closing only on
 *      resolve, holding the dialog open with the message inline on reject, and
 *      clearing itself on close.
 *   2. `ConfirmDialogContent` — the LAYOUT. The icon-led row, the message, the
 *      inline error box, and an `actions` slot.
 *
 * They are separate because the skin needs the pending flag BEFORE it renders
 * its modal: both skins gate their dismiss paths (backdrop / Escape / close X)
 * on `isPending`, and a single all-in-one component would have that state
 * trapped inside, below the modal it needs to configure. Splitting also keeps
 * the core ignorant of any app's `Button` — see the next note.
 *
 * WHY THE CORE RENDERS NO BUTTON: button vocabulary is a dialect concern (the
 * Canon says "button shape follows each app's own `Button` dialect"). Since
 * BL-1711 each app's `Button` is itself a skin over the shared `Button` core,
 * and re-expressing its variants here would fork that skin. The skin passes fully-formed nodes through `actions` instead —
 * the same call `EmptyState` makes for its `action` slot.
 *
 * WHY THIS CORE RENDERS NO MODAL: each app's skin supplies its own modal
 * wrapper (admin's mounts its `Modal` since BL-2624; bidder's mounts its own).
 * When this core was merged, admin's call sites still sat on an eager modal
 * that BL-1247 sequenced to drain later, so the core stayed modal-agnostic and
 * inside its own blast radius.
 *
 * TWO BEHAVIOURAL DRIFTS ARE FIXED HERE, both by taking bidder's half:
 *   - Admin kept `error` in state across a close, so the next open of the same
 *     dialog rendered a stale failure from the previous attempt.
 *   - Admin gated only the backdrop while a confirm was in flight, leaving
 *     Escape live; dismissing mid-request drops the pending rejection on the
 *     floor and silently loses the failure.
 */
'use client';

import clsx from 'clsx';
import { useState, type ElementType, type ReactNode } from 'react';

/** Class names that differ between the two visual variants. */
export interface ConfirmDialogVariantClasses {
    /** Applied when `variant="default"` (a cautionary, non-destructive confirm). */
    default?: string;
    /** Applied when `variant="danger"` (a destructive confirm). */
    danger?: string;
}

/**
 * Per-app appearance binding — class names and element choices only, no
 * structure. Every slot is optional: a consumer with no dialect gets the bare
 * accessible structure, which is the right default for an app that hasn't
 * declared one yet.
 */
export interface ConfirmDialogDialect {
    /** The outer content block — padding and vertical rhythm. */
    root?: string;
    /** The icon-led row holding the glyph and the text column. */
    header?: string;
    /** Wrapper around the icon — the tinted circle in both dialects. */
    glyph?: ConfirmDialogVariantClasses;
    /** Classes on the icon element itself. */
    icon?: ConfirmDialogVariantClasses;
    /** The text column beside the glyph (title + message). */
    body?: string;
    /** Classes on the title element. */
    title?: string;
    /** Classes on the message element. */
    message?: string;
    /** Classes on the inline error box. */
    error?: string;
    /** The action row holding the skin's buttons. */
    footer?: string;
    /**
     * Element the title renders as. Defaults to `h3` — admin's tag, and admin
     * is 44 of the 47 call sites, so the default is the no-DOM-change option.
     */
    titleAs?: 'h2' | 'h3';
}

/** Inputs to {@link useConfirmAction}. */
export interface UseConfirmActionOptions {
    /** Whether the dialog is currently open. Drives the reset-on-close. */
    isOpen: boolean;
    /** Called on cancel, and after a CONFIRMED-successful confirm. */
    onClose: () => void;
    /**
     * Runs when the user confirms. Awaited: resolve → `onClose` fires; throw →
     * the thrown Error's message is surfaced and the dialog stays open.
     * @sideEffect whatever the caller's confirm action does (e.g. delete a row).
     */
    onConfirm: () => void | Promise<void>;
    /**
     * Externally-controlled pending state, overriding the internal one. 11 admin
     * callers drive this themselves. `??`, not `||` — an explicit `false` is a
     * real value and must win over internal state.
     */
    isLoading?: boolean;
}

/** What {@link useConfirmAction} hands back to a skin. */
export interface ConfirmActionState {
    /** True while the confirm is in flight. Skins gate their dismiss paths on it. */
    isPending: boolean;
    /** The last failure message, or `null`. Pass straight to `ConfirmDialogContent`. */
    error: string | null;
    /** Click handler for the confirm button. Fire-and-forget; never throws. */
    confirm: () => void;
}

/** Shown when a rejection carries something that isn't an `Error`. */
export const CONFIRM_FALLBACK_ERROR = 'Something went wrong. Please try again.';

/**
 * Owns the close-only-on-confirmed-success contract.
 *
 * @param options - See {@link UseConfirmActionOptions}.
 * @returns Pending flag, inline error, and the confirm click handler.
 */
export function useConfirmAction({
    isOpen,
    onClose,
    onConfirm,
    isLoading,
}: UseConfirmActionOptions): ConfirmActionState {
    const [internalPending, setInternalPending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [wasOpen, setWasOpen] = useState(isOpen);

    // Reset transient state when the dialog transitions CLOSED, so a prior error
    // or pending flag can't carry into the next open. Done during render on the
    // prop change (React's "you might not need an effect" pattern) rather than in
    // a useEffect, which avoids a synchronous setState-in-effect and the extra
    // paint that comes with it.
    if (wasOpen !== isOpen) {
        setWasOpen(isOpen);
        if (!isOpen) {
            setInternalPending(false);
            setError(null);
        }
    }

    const isPending = isLoading ?? internalPending;

    const confirm = () => {
        setError(null);
        setInternalPending(true);
        void (async () => {
            try {
                await onConfirm();
            } catch (err) {
                // Stay open, surface the message inline, re-enable the buttons.
                setError(err instanceof Error ? err.message : CONFIRM_FALLBACK_ERROR);
                setInternalPending(false);
                return;
            }
            // Confirmed success. Clear pending BEFORE closing so a caller whose
            // `onClose` leaves the dialog mounted (rather than unmounting it)
            // doesn't get stuck with permanently disabled buttons.
            setInternalPending(false);
            onClose();
        })();
    };

    return { isPending, error, confirm };
}

/** Props for {@link ConfirmDialogContent}. */
export interface ConfirmDialogContentProps {
    /** Dialog heading. */
    title: string;
    /** Descriptive body text or content. */
    message: ReactNode;
    /** Inline failure message from {@link useConfirmAction}, or `null`. */
    error?: string | null;
    /** Icon component. Skins supply their own — the core holds no default. */
    icon?: ElementType;
    /** Visual variant. Defaults to `default`, matching admin's 44 call sites. */
    variant?: 'default' | 'danger';
    /** Per-app appearance binding. See {@link ConfirmDialogDialect}. */
    dialect?: ConfirmDialogDialect;
    /**
     * Id placed on the heading, so a skin whose modal draws no header of its own
     * can point `aria-labelledby` at the visible title instead of duplicating
     * the string into an `aria-label`.
     */
    titleId?: string;
    /** The skin's buttons, fully formed. See the file header. */
    actions: ReactNode;
    /** Extra classes on the root, composed with `dialect.root`. */
    className?: string;
    /**
     * The skin's automation-handle namespace (BL-1874). The core stamps only
     * `<testId>-error` on the inline refusal; the root and the actions belong
     * to the skin, which already tags them. Unset, the error carries nothing.
     */
    testId?: string;
}

/**
 * The confirm dialog's content — icon-led row, message, inline error, actions.
 *
 * Renders no modal, backdrop, or portal; the skin supplies those.
 *
 * @param props - See {@link ConfirmDialogContentProps}.
 * @returns The rendered confirm content in the supplied dialect.
 */
export default function ConfirmDialogContent({
    title,
    message,
    error = null,
    icon: Icon,
    variant = 'default',
    dialect = {},
    titleId,
    actions,
    className,
    testId,
}: ConfirmDialogContentProps) {
    const Title = dialect.titleAs ?? 'h3';
    const glyphClass = dialect.glyph?.[variant];

    // Built once so the "wrapped vs bare" decision below reads as one choice
    // rather than duplicating the icon element across two branches.
    const iconNode = Icon ? <Icon className={dialect.icon?.[variant]} aria-hidden /> : null;

    return (
        <div className={clsx(dialect.root, className)}>
            <div className={dialect.header}>
                {iconNode && (glyphClass ? <div className={glyphClass}>{iconNode}</div> : iconNode)}
                <div className={dialect.body}>
                    <Title id={titleId} className={dialect.title}>{title}</Title>
                    {/* A title-only confirm leaves no empty spacer under its title. */}
                    {message != null && message !== '' && <div className={dialect.message}>{message}</div>}
                </div>
            </div>

            {/*
              * `!= null && !== ''` rather than a truthiness check: an empty-string
              * error must render nothing, but the check must not treat other
              * falsy-looking values as absent. See company-standards.md "0 is a value".
              */}
            {error != null && error !== '' && (
                <p
                    className={dialect.error}
                    role="alert"
                    data-testid={testId ? `${testId}-error` : undefined}
                >
                    {error}
                </p>
            )}

            <div className={dialect.footer}>{actions}</div>
        </div>
    );
}
